import { useState } from 'react';
import { MapPin, Eye, EyeOff, Maximize2 } from 'lucide-react';

/**
 * DestinationMapPreview — satellite + street-level preview of a shipping destination.
 *
 * Uses the free Google Maps embed (no API key required for basic embed).
 * Also offers a "Street View" toggle so the merchant can see the actual building.
 *
 * Props:
 *   street, city, state, zip, country   destination fields
 *   defaultOpen                          expand by default
 */
export default function DestinationMapPreview({ street, city, state, zip, country = 'US', testid = 'dest-map' }) {
  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState('satellite'); // satellite | street

  const parts = [street, city, state, zip, country].filter(Boolean).join(', ');
  const hasEnough = !!(zip || (city && state));
  const encoded = encodeURIComponent(parts);

  // Free Google Maps embed URLs — no API key needed for these variants.
  // t=k → satellite, t=m → map, layer=c → street view
  const satelliteUrl = `https://maps.google.com/maps?q=${encoded}&t=k&z=18&ie=UTF8&iwloc=&output=embed`;
  const streetUrl    = `https://maps.google.com/maps?q=${encoded}&t=m&layer=c&cbll=&cbp=12,20.09,,0,5&output=svembed`;
  const openInNew    = `https://www.google.com/maps/search/?api=1&query=${encoded}`;

  if (!hasEnough) return null;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-semibold">Destination preview</span>
          <span className="text-xs text-slate-500 truncate max-w-[200px] hidden sm:inline">· {parts}</span>
        </div>
        {open ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-900/70">
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setMode('satellite')}
                className={`text-xs px-2 py-1 rounded ${mode === 'satellite' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'}`}
                data-testid={`${testid}-satellite`}
              >
                Satellite
              </button>
              <button
                type="button"
                onClick={() => setMode('street')}
                className={`text-xs px-2 py-1 rounded ${mode === 'street' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'}`}
                data-testid={`${testid}-street`}
              >
                Street level
              </button>
            </div>
            <a
              href={openInNew}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1"
              data-testid={`${testid}-openmap`}
            >
              <Maximize2 className="w-3 h-3" /> Open in Maps
            </a>
          </div>
          <iframe
            key={mode}
            title="Destination map"
            src={mode === 'satellite' ? satelliteUrl : streetUrl}
            width="100%"
            height="260"
            className="border-0 block"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid={`${testid}-iframe`}
          />
        </div>
      )}
    </div>
  );
}
